/**
 * VPE Management Feature Service
 * Handles VPE (Virtual Patient Enroller) business logic and API operations
 * Used by EA role service for VPE CRUD operations
 */

import { AbstractService } from '@/services/core/serviceFactory';
import { usersGraphqlService } from '@/services/api';

export interface VPE {
  hbox_user_id: number;
  first_name: string;
  last_name: string;
  es_name: string;
  email: string;
  deferred_task: boolean;
}

export interface CreateVPEData {
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  task_assigned: string | null;
}

export interface UpdateVPEData {
  first_name: string;
  last_name: string;
  email: string;
  password?: string;
  task_assigned: string | null;
}

export interface VPEResponse {
  success: boolean;
  message?: string;
  data?: any;
}

export class VPEManagementService extends AbstractService {
  /**
   * Fetch all VPEs (Virtual Patient Enrollers)
   */
  async fetchVPEs(): Promise<VPE[]> {
    this.requireAuth();

    try {
      console.log('VPEManagementService: Fetching VPEs with hasura token');
      const result = await usersGraphqlService.getEnrollmentSpecialistsList(this.context.hasuraToken!);

      const vpes = (result.data?.pep_vw_enrollment_specialists || []) as unknown as VPE[];
      console.log('VPEManagementService: Successfully fetched VPEs:', vpes.length);
      return vpes;
    } catch (error) {
      console.error('VPEManagementService: Error fetching VPEs:', error);
      throw new Error('Failed to fetch VPEs');
    }
  }

  /**
   * Create a new VPE
   */
  async createVPE(vpeData: CreateVPEData): Promise<VPEResponse> {
    this.requireUserId();
    this.requireAuth();

    try {
      console.log('VPEManagementService: Creating VPE');
      const response = await this.apiClient.post(`/api/pep/${this.context.userId}/ea`, vpeData, this.context.token);
      
      return {
        success: true,
        message: 'VPE created successfully',
        data: response.data
      };
    } catch (error) {
      console.error('VPEManagementService: Error creating VPE:', error);
      throw new Error('Failed to create VPE');
    }
  }

  /**
   * Update an existing VPE
   */
  async updateVPE(vpeId: number, vpeData: UpdateVPEData): Promise<VPEResponse> {
    this.requireUserId();
    this.requireAuth();

    try {
      console.log('VPEManagementService: Updating VPE');
      const response = await this.apiClient.put(`/api/pep/${this.context.userId}/ea/${vpeId}`, vpeData, this.context.token);
      
      return {
        success: true,
        message: 'VPE updated successfully',
        data: response.data
      };
    } catch (error) {
      console.error('VPEManagementService: Error updating VPE:', error);
      throw new Error('Failed to update VPE');
    }
  }

  /**
   * Delete a VPE
   */
  async deleteVPE(vpeId: number): Promise<VPEResponse> {
    this.requireUserId();
    this.requireAuth();

    try {
      console.log('VPEManagementService: Deleting VPE');
      const response = await this.apiClient.delete(`/api/pep/${this.context.userId}/ea/${vpeId}`, this.context.token);

      return {
        success: true,
        message: 'VPE deleted successfully',
        data: response.data
      };
    } catch (error) {
      console.error('VPEManagementService: Error deleting VPE:', error);
      throw new Error('Failed to delete VPE');
    }
  }

  /**
   * Validate VPE form data
   */
  validateVPEData(data: Partial<CreateVPEData | UpdateVPEData>, isEditMode: boolean = false): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    // Name validations - allow letters, numbers, and spaces
    const nameRegex = /^[A-Za-z0-9\s]+$/;

    if (!data.first_name?.trim() || !nameRegex.test(data.first_name)) {
      errors.push("First name is required and should only contain letters, numbers, and spaces.");
    }

    if (!data.last_name?.trim() || !nameRegex.test(data.last_name)) {
      errors.push("Last name is required and should only contain letters, numbers, and spaces.");
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!data.email?.trim() || !emailRegex.test(data.email)) {
      errors.push("Please enter a valid email address.");
    }

    // Password validation
    if (!isEditMode) {
      // For create mode, password is required
      if (!data.password || data.password.length < 6) {
        errors.push("Password must be at least 6 characters long.");
      }
    } else {
      // For edit mode, password is optional but must be valid if provided
      if (data.password && data.password.length < 6) {
        errors.push("Password must be at least 6 characters long.");
      }
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Check if VPE data has changes (for edit mode)
   */
  hasChanges(originalVPE: VPE, newData: UpdateVPEData): boolean {
    return (
      newData.first_name.trim() !== originalVPE.first_name ||
      newData.last_name.trim() !== originalVPE.last_name ||
      newData.email.trim() !== originalVPE.email ||
      (newData.task_assigned === "deferred") !== originalVPE.deferred_task ||
      (newData.password && newData.password.trim() !== '')
    );
  }
}